// apps/client/src/components/Workspace/WorkspaceSettings/panels/ShortcutsPanel.tsx
import { useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { Kbd, KbdGroup, CmdCtrl, OptAlt } from '@duck/ui'
import { useTar } from '@/modules'
import type { TranslationKey } from '@/modules/i18n'
import {
  shortcutRegistryAtom,
  type ShortcutDefinition,
  type ShortcutGroup,
  type ShortcutKey,
} from '@/modules/Shortcuts'

const GROUP_ORDER: ShortcutGroup[] = ['tabs', 'editor', 'notebook', 'general']

const GROUP_LABEL_KEYS: Record<ShortcutGroup, TranslationKey> = {
  tabs: 'common.shortcutsGroupTabs',
  general: 'common.shortcutsGroupGeneral',
  editor: 'common.shortcutsGroupEditor',
  notebook: 'common.shortcutsGroupNotebook',
}

const KEY_GLYPHS: Record<string, string> = {
  Enter: '↵',
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  Backspace: '⌫',
  Escape: 'Esc',
  Num: 'Num',
}

const renderKey = (key: string): string => KEY_GLYPHS[key] ?? key.toUpperCase()

// Collapse a set of bindings that share modifiers and differ only by single-digit
// keys (e.g. ⌥1..⌥9) into one synthetic binding with key "Num" for display.
const collapseDigitBindings = (bindings: ShortcutKey[]): ShortcutKey | null => {
  if (bindings.length < 2) return null
  const first = bindings[0]
  const allDigits = bindings.every(
    (b) =>
      /^[0-9]$/.test(b.key) &&
      !!b.cmd === !!first.cmd &&
      !!b.shift === !!first.shift &&
      !!b.alt === !!first.alt,
  )
  if (!allDigits) return null
  return { ...first, key: 'Num' }
}

const pickDisplayBinding = (keys: ShortcutKey | ShortcutKey[]): ShortcutKey => {
  if (!Array.isArray(keys)) return keys
  return collapseDigitBindings(keys) ?? keys[0]
}

const renderBinding = (binding: ShortcutKey) => (
  <KbdGroup>
    {binding.cmd && <CmdCtrl />}
    {binding.alt && <OptAlt />}
    {binding.shift && <Kbd>⇧</Kbd>}
    <Kbd>{renderKey(binding.key)}</Kbd>
  </KbdGroup>
)

interface ShortcutRowProps {
  def: ShortcutDefinition
  t: (key: TranslationKey) => string
}

const ShortcutRow = ({ def, t }: ShortcutRowProps) => (
  <div className="flex items-center justify-between gap-4 px-1 py-1.5 border-b border-border/40 text-sm">
    <span className="text-foreground">{t(def.labelKey as TranslationKey)}</span>
    {renderBinding(pickDisplayBinding(def.keys))}
  </div>
)

export const ShortcutsPanel = () => {
  const { t } = useTar()
  const registry = useAtomValue(shortcutRegistryAtom)

  const grouped = useMemo(() => {
    const map: Record<ShortcutGroup, ShortcutDefinition[]> = {
      tabs: [],
      general: [],
      editor: [],
      notebook: [],
    }
    for (const def of Object.values(registry)) {
      map[def.group].push(def)
    }
    return map
  }, [registry])

  return (
    <div className="flex flex-col gap-3">
      <div className="text-lg font-semibold text-foreground">
        {t('common.shortcutsTitle')}
      </div>

      {GROUP_ORDER.map((group) => {
        const rows = grouped[group]
        if (rows.length === 0) return null
        return (
          <div key={group} className="flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {t(GROUP_LABEL_KEYS[group])}
              </div>
              {group === 'tabs' && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  (<OptAlt />)
                </span>
              )}
            </div>
            <div>
              {rows.map((def) => (
                <ShortcutRow key={def.id} def={def} t={t} />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default ShortcutsPanel
